import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  LayoutDashboard,
  UploadCloud,
  FileText,
  Users,
  Building2,
  Settings,
  LogOut,
  BadgeEuro,
  TrendingUp,
  TrendingDown,
  Link as LinkIcon,
  ChevronRight,
  Search,
  Filter,
  X,
  Plus,
  Trash2,
  Edit,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Note: For enhanced security, consider using httpOnly cookies via backend
    // localStorage is used here for simplicity but is vulnerable to XSS
    return sessionStorage.getItem("token") || localStorage.getItem("token");
  });
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = useCallback(async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, user: userData } = response.data;
    // Use sessionStorage for better security (cleared when browser closes)
    sessionStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const authContextValue = useMemo(() => ({
    user, token, login, logout, loading
  }), [user, token, login, logout, loading]);

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2
  }).format(amount || 0);
};

// Format percentage
const formatPercent = (value) => {
  return `${(value || 0).toFixed(2)}%`;
};

// Login Page
const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Welcome to The Ledger");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/seed`);
      toast.success("Database seeded! Use admin/admin123 to login");
    } catch (error) {
      toast.error("Seeding failed or already done");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen login-gradient flex items-center justify-center p-4">
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-emerald-900 rounded-lg flex items-center justify-center">
              <BadgeEuro className="w-7 h-7 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-zinc-900">The Ledger</CardTitle>
          <CardDescription>Commission Reconciliation System</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit"
              className="w-full bg-emerald-900 hover:bg-emerald-800"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
          <div className="mt-6 pt-6 border-t border-zinc-200">
            <Button
              variant="outline"
              data-testid="seed-database"
              className="w-full"
              onClick={handleSeed}
              disabled={seeding}
            >
              {seeding ? "Initializing..." : "Initialize Database (First Time)"}
            </Button>
            <p className="text-xs text-zinc-500 text-center mt-2">
              Creates admin user and sample data
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Sidebar Component
const Sidebar = () => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/upload", icon: UploadCloud, label: "Upload Statement" },
    { path: "/statements", icon: FileText, label: "Statements" },
    { path: "/sales-reps", icon: Users, label: "Sales Reps" },
    { path: "/providers", icon: Building2, label: "Providers" },
    { path: "/commission-codes", icon: LinkIcon, label: "Code Mapping" },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="sidebar">
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
            <BadgeEuro className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-lg font-manrope">The Ledger</h1>
            <p className="text-xs text-zinc-500">Commission System</p>
          </div>
        </div>
      </div>

      <nav className="p-2 mt-4 space-y-1">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            className={`sidebar-link ${location.pathname === item.path ? "active" : ""}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>

      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium">{user?.username?.charAt(0).toUpperCase()}</span>
            </div>
            <span className="text-sm">{user?.username}</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            data-testid="logout-btn"
            onClick={handleLogout}
            className="text-zinc-400 hover:text-white"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Layout Component
const Layout = ({ children }) => {
  return (
    <div className="flex min-h-screen bg-zinc-50">
      <Sidebar />
      <main className="ml-64 flex-1 p-8">
        {children}
      </main>
    </div>
  );
};

// Chart style constants (extracted to prevent re-renders)
const CHART_COLORS = ["#059669", "#2563eb", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];
const CHART_TICK_STYLE = { fontSize: 12 };
const CHART_TOOLTIP_STYLE = { background: "white", border: "1px solid #e4e4e7", borderRadius: "8px" };
const BAR_RADIUS = [4, 4, 0, 0];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Dashboard Page
const DashboardPage = () => {
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [byRep, setByRep] = useState([]);
  const [byProvider, setByProvider] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [summaryRes, trendsRes, byRepRes, byProviderRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary`),
        axios.get(`${API}/dashboard/monthly-trends`),
        axios.get(`${API}/dashboard/by-rep`),
        axios.get(`${API}/dashboard/by-provider`)
      ]);
      setSummary(summaryRes.data);
      setTrends(trendsRes.data);
      setByRep(byRepRes.data);
      setByProvider(byProviderRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-500">Loading dashboard...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard">
        <div>
          <h1 className="text-3xl font-bold text-zinc-900">Dashboard</h1>
          <p className="text-zinc-500 mt-1">Commission overview and analytics</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card data-testid="card-monthly-gross">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">This Month (Gross)</p>
                  <p className="text-2xl font-bold font-mono tabular-nums mt-1">
                    {formatCurrency(summary?.current_month?.total_gross)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <p className="text-xs text-zinc-400 mt-2">
                {MONTH_NAMES[(summary?.current_month?.month || 1) - 1]} {summary?.current_month?.year}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="card-monthly-net">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">This Month (Net Payable)</p>
                  <p className="text-2xl font-bold font-mono tabular-nums mt-1">
                    {formatCurrency(summary?.current_month?.total_net)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                  <BadgeEuro className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-zinc-400 mt-2">
                {summary?.current_month?.records_count || 0} records
              </p>
            </CardContent>
          </Card>

          <Card data-testid="card-rolling-gross">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Rolling 12 Months (Gross)</p>
                  <p className="text-2xl font-bold font-mono tabular-nums mt-1">
                    {formatCurrency(summary?.rolling_12_months?.total_gross)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <p className="text-xs text-zinc-400 mt-2">
                {summary?.rolling_12_months?.records_count || 0} total records
              </p>
            </CardContent>
          </Card>

          <Card data-testid="card-stats">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Active Entities</p>
                  <p className="text-2xl font-bold mt-1">
                    {summary?.counts?.sales_reps || 0} / {summary?.counts?.providers || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-zinc-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-zinc-600" />
                </div>
              </div>
              <p className="text-xs text-zinc-400 mt-2">
                Reps / Providers • {summary?.counts?.statements || 0} statements
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Monthly Trends Chart */}
          <Card data-testid="chart-trends">
            <CardHeader>
              <CardTitle>Monthly Commission Trends</CardTitle>
              <CardDescription>Gross commissions by month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72 min-w-0">
                {trends.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="month" tick={CHART_TICK_STYLE} />
                      <YAxis tick={CHART_TICK_STYLE} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={CHART_TOOLTIP_STYLE}
                      />
                      <Bar dataKey="total_gross" fill="#059669" radius={BAR_RADIUS} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-zinc-400">
                    No data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* By Commission Type */}
          <Card data-testid="chart-by-type">
            <CardHeader>
              <CardTitle>Commission by Type</CardTitle>
              <CardDescription>Current month breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72 min-w-0">
                {summary?.by_commission_type?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={summary.by_commission_type}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="total"
                        nameKey="_id"
                        label={({ _id, percent }) => `${_id} ${(percent * 100).toFixed(0)}%`}
                      >
                        {summary.by_commission_type.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-zinc-400">
                    No data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tables Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* By Sales Rep */}
          <Card data-testid="table-by-rep">
            <CardHeader>
              <CardTitle>Commissions by Sales Rep</CardTitle>
              <CardDescription>Total commissions earned</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sales Rep</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Net Payable</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byRep.length > 0 ? byRep.map((rep) => (
                    <TableRow key={rep._id || "unallocated"}>
                      <TableCell className="font-medium">{rep.sales_rep_name || "Unallocated"}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{formatCurrency(rep.total_gross)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{formatCurrency(rep.total_net)}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-zinc-400">No data</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* By Provider */}
          <Card data-testid="table-by-provider">
            <CardHeader>
              <CardTitle>Commissions by Provider</CardTitle>
              <CardDescription>Total commissions received</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Records</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byProvider.length > 0 ? byProvider.map((prov) => (
                    <TableRow key={prov._id}>
                      <TableCell className="font-medium">{prov.provider_name}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{formatCurrency(prov.total_gross)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{prov.records_count}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-zinc-400">No data</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

// Upload Page
const UploadPage = () => {
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchProviders = useCallback(async () => {
    setProvidersLoading(true);
    try {
      const response = await axios.get(`${API}/providers`);
      console.log('Providers loaded:', response.data?.length || 0);
      setProviders(response.data || []);
    } catch (error) {
      console.error('Failed to load providers:', error);
      toast.error("Failed to load providers - please refresh the page");
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleUpload = async (e) => {
    e.preventDefault();
    
    // Debug logging for validation
    console.log('Upload validation:', { file: !!file, selectedProvider, selectedMonth, selectedYear });
    
    // Validate each field with specific messages
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }
    if (!selectedProvider) {
      toast.error("Please select a provider");
      return;
    }
    if (!selectedMonth) {
      toast.error("Please select a statement month");
      return;
    }
    if (!selectedYear) {
      toast.error("Please select a statement year");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("provider_id", selectedProvider);
    formData.append("statement_month", selectedMonth);
    formData.append("statement_year", parseInt(selectedYear));

    try {
      const response = await axios.post(`${API}/statements/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`Uploaded! ${response.data.records_processed} records processed`);
      setFile(null);
      setSelectedProvider("");
      setSelectedMonth("");
      // Reset file input
      const fileInput = document.getElementById('file');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const years = Array.from({ length: 10 }, (_, i) => (new Date().getFullYear() - i).toString());

  return (
    <Layout>
      <div className="max-w-2xl animate-fade-in" data-testid="upload-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-zinc-900">Upload Statement</h1>
          <p className="text-zinc-500 mt-1">Upload commission statements from providers</p>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleUpload} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="provider">Provider</Label>
                <Select value={selectedProvider} onValueChange={setSelectedProvider} disabled={providersLoading}>
                  <SelectTrigger data-testid="select-provider">
                    <SelectValue placeholder={providersLoading ? "Loading providers..." : (providers.length === 0 ? "No providers found" : "Select provider")} />
                  </SelectTrigger>
                  <SelectContent>
                    {providers.length === 0 ? (
                      <SelectItem value="__none__" disabled>No providers available</SelectItem>
                    ) : (
                      providers.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {!providersLoading && providers.length === 0 && (
                  <p className="text-xs text-amber-600">No providers found. Please run /api/seed to initialize the database.</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="month">Statement Month</Label>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger data-testid="select-month">
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="year">Statement Year</Label>
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger data-testid="select-year">
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="file">Commission File (CSV or Excel)</Label>
                <div 
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                    file ? 'border-emerald-500 bg-emerald-50' : 'border-zinc-200 hover:border-emerald-500'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.classList.add('border-emerald-500', 'bg-emerald-50');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!file) {
                      e.currentTarget.classList.remove('border-emerald-500', 'bg-emerald-50');
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const droppedFile = e.dataTransfer.files?.[0];
                    if (droppedFile) {
                      const validTypes = ['.csv', '.xlsx', '.xls'];
                      const fileExt = droppedFile.name.toLowerCase().slice(droppedFile.name.lastIndexOf('.'));
                      if (validTypes.includes(fileExt)) {
                        console.log('File dropped:', droppedFile.name);
                        setFile(droppedFile);
                      } else {
                        toast.error('Please drop a CSV or Excel file');
                      }
                    }
                  }}
                  onClick={() => document.getElementById('file')?.click()}
                >
                  <input
                    type="file"
                    id="file"
                    data-testid="file-input"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                      const selectedFile = e.target.files?.[0];
                      console.log('File selected:', selectedFile?.name);
                      setFile(selectedFile || null);
                    }}
                    className="hidden"
                  />
                  <UploadCloud className={`w-12 h-12 mx-auto mb-4 ${file ? 'text-emerald-600' : 'text-zinc-400'}`} />
                  {file ? (
                    <div>
                      <p className="text-emerald-600 font-medium">{file.name}</p>
                      <p className="text-sm text-emerald-500 mt-1">Click or drag to change file</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-zinc-600 font-medium">Click to upload or drag and drop</p>
                      <p className="text-sm text-zinc-400 mt-1">CSV or Excel files</p>
                    </>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                data-testid="upload-submit"
                className="w-full bg-emerald-900 hover:bg-emerald-800"
                disabled={uploading}
              >
                {uploading ? "Processing..." : "Upload & Process Statement"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

// Statements Page
const StatementsPage = () => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStatements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/statements`);
      setStatements(response.data);
    } catch (error) {
      toast.error("Failed to load statements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatements();
  }, [fetchStatements]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Delete this statement and all related commissions?")) return;
    try {
      await axios.delete(`${API}/statements/${id}`);
      toast.success("Statement deleted");
      fetchStatements();
    } catch (error) {
      toast.error("Failed to delete statement");
    }
  }, [fetchStatements]);

  // Memoize expensive calculations
  const { totalStatements, totalRecords, totalCommission, uniqueProviders } = useMemo(() => ({
    totalStatements: statements.length,
    totalRecords: statements.reduce((sum, s) => sum + (s.records_count || 0), 0),
    totalCommission: statements.reduce((sum, s) => sum + (s.total_commission || 0), 0),
    uniqueProviders: [...new Set(statements.map(s => s.provider_name))].length
  }), [statements]);

  // Memoize provider breakdown with sorting
  const sortedProviderBreakdown = useMemo(() => {
    const breakdown = statements.reduce((acc, s) => {
      const key = s.provider_name;
      if (!acc[key]) acc[key] = { count: 0, records: 0, commission: 0 };
      acc[key].count += 1;
      acc[key].records += s.records_count || 0;
      acc[key].commission += s.total_commission || 0;
      return acc;
    }, {});
    return Object.entries(breakdown).sort((a, b) => b[1].commission - a[1].commission);
  }, [statements]);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="statements-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-zinc-900">Statements</h1>
            <p className="text-zinc-500 mt-1">Track all uploaded commission statements</p>
          </div>
          <Link to="/upload">
            <Button className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-upload-new">
              <UploadCloud className="w-4 h-4 mr-2" />
              Upload New
            </Button>
          </Link>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-emerald-600">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Total Statements</p>
                  <p className="text-2xl font-bold text-zinc-900" data-testid="total-statements">{totalStatements}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-blue-600">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Providers Covered</p>
                  <p className="text-2xl font-bold text-zinc-900" data-testid="unique-providers">{uniqueProviders}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-amber-600">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Total Records</p>
                  <p className="text-2xl font-bold text-zinc-900" data-testid="total-records">{totalRecords.toLocaleString()}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-violet-600">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Total Commission</p>
                  <p className="text-2xl font-bold text-zinc-900" data-testid="total-commission">{formatCurrency(totalCommission)}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                  <BadgeEuro className="w-5 h-5 text-violet-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Provider Breakdown */}
        {sortedProviderBreakdown.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Commission by Provider</CardTitle>
              <CardDescription>Breakdown of uploaded statements per provider</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {sortedProviderBreakdown.map(([provider, data]) => (
                  <div key={provider} className="p-3 rounded-lg bg-zinc-50 border border-zinc-100">
                    <p className="font-medium text-zinc-800 truncate">{provider}</p>
                    <p className="text-lg font-bold text-emerald-700 tabular-nums">{formatCurrency(data.commission)}</p>
                    <p className="text-xs text-zinc-500">{data.count} statement{data.count > 1 ? 's' : ''} • {data.records} records</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Statements Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">All Uploaded Statements</CardTitle>
            <CardDescription>Detailed list of all commission statements</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Filename</TableHead>
                  <TableHead className="text-right">Records</TableHead>
                  <TableHead className="text-right">Total Commission</TableHead>
                  <TableHead>Upload Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statements.length > 0 ? statements.map((stmt) => (
                  <TableRow key={stmt.id} className="hover:bg-zinc-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-xs">
                          {stmt.provider_name?.slice(0, 2).toUpperCase()}
                        </div>
                        <span className="font-medium">{stmt.provider_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {stmt.statement_month} {stmt.statement_year}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-500 text-sm max-w-[200px] truncate">{stmt.filename}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{stmt.records_count?.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums font-medium text-emerald-700">{formatCurrency(stmt.total_commission)}</TableCell>
                    <TableCell className="text-sm text-zinc-500">
                      {new Date(stmt.upload_date).toLocaleDateString('en-IE', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric' 
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`delete-statement-${stmt.id}`}
                        onClick={() => handleDelete(stmt.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-zinc-400">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No statements uploaded yet</p>
                      <p className="text-sm mt-1">Click "Upload New" to add your first commission statement</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

// Sales Reps Page
const SalesRepsPage = () => {
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRep, setEditingRep] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", commission_rate: 60 });
  const [selectedRepId, setSelectedRepId] = useState(null);
  const [repStatement, setRepStatement] = useState(null);

  const fetchReps = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sales-reps`);
      setReps(response.data);
    } catch (error) {
      toast.error("Failed to load sales reps");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReps();
  }, [fetchReps]);

  const handleSave = useCallback(async () => {
    try {
      if (editingRep) {
        await axios.put(`${API}/sales-reps/${editingRep.id}`, formData);
        toast.success("Sales rep updated");
      } else {
        await axios.post(`${API}/sales-reps`, formData);
        toast.success("Sales rep created");
      }
      setDialogOpen(false);
      setEditingRep(null);
      setFormData({ name: "", email: "", commission_rate: 60 });
      fetchReps();
    } catch (error) {
      toast.error("Failed to save sales rep");
    }
  }, [editingRep, formData, fetchReps]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Deactivate this sales rep?")) return;
    try {
      await axios.delete(`${API}/sales-reps/${id}`);
      toast.success("Sales rep deactivated");
      fetchReps();
    } catch (error) {
      toast.error("Failed to delete sales rep");
    }
  }, [fetchReps]);

  const viewStatement = useCallback(async (repId) => {
    try {
      const response = await axios.get(`${API}/rep-statement/${repId}`);
      setRepStatement(response.data);
      setSelectedRepId(repId);
    } catch (error) {
      toast.error("Failed to load statement");
    }
  }, []);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="sales-reps-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-zinc-900">Sales Representatives</h1>
            <p className="text-zinc-500 mt-1">Manage your sales team</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-emerald-900 hover:bg-emerald-800"
                data-testid="btn-add-rep"
                onClick={() => {
                  setEditingRep(null);
                  setFormData({ name: "", email: "", commission_rate: 60 });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Sales Rep
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingRep ? "Edit Sales Rep" : "Add Sales Rep"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    data-testid="input-rep-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="input-rep-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rate">Commission Rate (%)</Label>
                  <Input
                    id="rate"
                    data-testid="input-rep-rate"
                    type="number"
                    value={formData.commission_rate}
                    onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) })}
                    min={0}
                    max={100}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-save-rep" onClick={handleSave}>
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reps.map((rep) => (
            <Card key={rep.id} className="overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <span className="text-emerald-700 font-bold text-lg">
                        {rep.name.split(" ").map(n => n[0]).join("")}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-zinc-900">{rep.name}</h3>
                      <p className="text-sm text-zinc-500">{rep.email || "No email"}</p>
                    </div>
                  </div>
                  <Badge className="badge-success">{formatPercent(rep.commission_rate)}</Badge>
                </div>
                <div className="mt-4 pt-4 border-t border-zinc-100 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`view-rep-${rep.id}`}
                    onClick={() => viewStatement(rep.id)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Statement
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`edit-rep-${rep.id}`}
                    onClick={() => {
                      setEditingRep(rep);
                      setFormData({ name: rep.name, email: rep.email || "", commission_rate: rep.commission_rate });
                      setDialogOpen(true);
                    }}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`delete-rep-${rep.id}`}
                    className="text-red-500"
                    onClick={() => handleDelete(rep.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Rep Statement Dialog */}
        <Dialog open={!!selectedRepId} onOpenChange={() => setSelectedRepId(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>{repStatement?.sales_rep?.name} - Commission Statement</DialogTitle>
            </DialogHeader>
            {repStatement && (
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-zinc-500">Total Gross</p>
                      <p className="text-xl font-bold font-mono">{formatCurrency(repStatement.summary.total_gross)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-zinc-500">Total Net</p>
                      <p className="text-xl font-bold font-mono">{formatCurrency(repStatement.summary.total_net)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-zinc-500">Payable Gross</p>
                      <p className="text-xl font-bold font-mono text-emerald-600">{formatCurrency(repStatement.summary.payable_gross)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-zinc-500">Payable Net</p>
                      <p className="text-xl font-bold font-mono text-emerald-600">{formatCurrency(repStatement.summary.payable_net)}</p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">By Commission Type</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Gross</TableHead>
                          <TableHead className="text-right">Net</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(repStatement.by_type).map(([type, data]) => (
                          <TableRow key={type}>
                            <TableCell className="font-medium">{type}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(data.gross)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(data.net)}</TableCell>
                            <TableCell className="text-right font-mono">{data.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Provider Breakdown Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Commission by Provider</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Provider</TableHead>
                          <TableHead className="text-right">Gross</TableHead>
                          <TableHead className="text-right">Payable (60%)</TableHead>
                          <TableHead className="text-right">Records</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {repStatement.by_provider && Object.entries(repStatement.by_provider)
                          .sort((a, b) => b[1].gross - a[1].gross)
                          .map(([provider, data]) => (
                          <TableRow key={provider}>
                            <TableCell className="font-medium">{provider}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(data.gross)}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-600">{formatCurrency(data.payable_net)}</TableCell>
                            <TableCell className="text-right font-mono">{data.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// Providers Page
const ProvidersPage = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [formData, setFormData] = useState({ name: "", code: "" });

  const fetchProviders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/providers`);
      setProviders(response.data);
    } catch (error) {
      toast.error("Failed to load providers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleSave = useCallback(async () => {
    try {
      if (editingProvider) {
        await axios.put(`${API}/providers/${editingProvider.id}`, formData);
        toast.success("Provider updated");
      } else {
        await axios.post(`${API}/providers`, formData);
        toast.success("Provider created");
      }
      setDialogOpen(false);
      setEditingProvider(null);
      setFormData({ name: "", code: "" });
      fetchProviders();
    } catch (error) {
      toast.error("Failed to save provider");
    }
  }, [editingProvider, formData, fetchProviders]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Deactivate this provider?")) return;
    try {
      await axios.delete(`${API}/providers/${id}`);
      toast.success("Provider deactivated");
      fetchProviders();
    } catch (error) {
      toast.error("Failed to delete provider");
    }
  }, [fetchProviders]);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="providers-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-zinc-900">Providers</h1>
            <p className="text-zinc-500 mt-1">Commission statement providers</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-emerald-900 hover:bg-emerald-800"
                data-testid="btn-add-provider"
                onClick={() => {
                  setEditingProvider(null);
                  setFormData({ name: "", code: "" });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Provider
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingProvider ? "Edit Provider" : "Add Provider"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    data-testid="input-provider-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Provider name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Code (Optional)</Label>
                  <Input
                    id="code"
                    data-testid="input-provider-code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="Short code"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-save-provider" onClick={handleSave}>
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers.map((provider) => (
                  <TableRow key={provider.id}>
                    <TableCell className="font-medium">{provider.name}</TableCell>
                    <TableCell className="font-mono text-zinc-500">{provider.code || "-"}</TableCell>
                    <TableCell>
                      <Badge className="badge-success">Active</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`edit-provider-${provider.id}`}
                        onClick={() => {
                          setEditingProvider(provider);
                          setFormData({ name: provider.name, code: provider.code || "" });
                          setDialogOpen(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`delete-provider-${provider.id}`}
                        className="text-red-500"
                        onClick={() => handleDelete(provider.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

// Commission Codes Page
const CommissionCodesPage = () => {
  const [codes, setCodes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ code: "", provider_id: "", sales_rep_id: "" });
  const [filterProvider, setFilterProvider] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const [codesRes, providersRes, repsRes] = await Promise.all([
        axios.get(`${API}/commission-codes`),
        axios.get(`${API}/providers`),
        axios.get(`${API}/sales-reps`)
      ]);
      setCodes(codesRes.data);
      setProviders(providersRes.data);
      setReps(repsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = useCallback(async () => {
    try {
      await axios.post(`${API}/commission-codes`, formData);
      toast.success("Commission code mapping created");
      setDialogOpen(false);
      setFormData({ code: "", provider_id: "", sales_rep_id: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create mapping");
    }
  }, [formData, fetchData]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Delete this commission code mapping?")) return;
    try {
      await axios.delete(`${API}/commission-codes/${id}`);
      toast.success("Mapping deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete mapping");
    }
  }, [fetchData]);

  const filteredCodes = useMemo(() => 
    filterProvider === "all" ? codes : codes.filter(c => c.provider_id === filterProvider),
    [codes, filterProvider]
  );

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="commission-codes-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-zinc-900">Commission Code Mapping</h1>
            <p className="text-zinc-500 mt-1">Map provider codes to sales representatives</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-emerald-900 hover:bg-emerald-800"
                data-testid="btn-add-code"
                onClick={() => setFormData({ code: "", provider_id: "", sales_rep_id: "" })}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Mapping
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Commission Code Mapping</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="provider">Provider</Label>
                  <Select value={formData.provider_id} onValueChange={(v) => setFormData({ ...formData, provider_id: v })}>
                    <SelectTrigger data-testid="select-mapping-provider">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Commission Code</Label>
                  <Input
                    id="code"
                    data-testid="input-mapping-code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="Enter code (e.g., 20277, ABC10894)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rep">Sales Representative</Label>
                  <Select value={formData.sales_rep_id} onValueChange={(v) => setFormData({ ...formData, sales_rep_id: v })}>
                    <SelectTrigger data-testid="select-mapping-rep">
                      <SelectValue placeholder="Select sales rep" />
                    </SelectTrigger>
                    <SelectContent>
                      {reps.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-save-mapping" onClick={handleSave}>
                  Save Mapping
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-400" />
            <Select value={filterProvider} onValueChange={setFilterProvider}>
              <SelectTrigger className="w-48" data-testid="filter-provider">
                <SelectValue placeholder="Filter by provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Providers</SelectItem>
                {providers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Badge variant="secondary">{filteredCodes.length} mappings</Badge>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Commission Code</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Sales Representative</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCodes.length > 0 ? filteredCodes.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell className="font-mono font-medium">{code.code}</TableCell>
                    <TableCell>{code.provider_name}</TableCell>
                    <TableCell>{code.sales_rep_name}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`delete-code-${code.id}`}
                        className="text-red-500"
                        onClick={() => handleDelete(code.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-zinc-400">
                      No commission codes mapped
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Main App
function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
          <Route path="/statements" element={<ProtectedRoute><StatementsPage /></ProtectedRoute>} />
          <Route path="/sales-reps" element={<ProtectedRoute><SalesRepsPage /></ProtectedRoute>} />
          <Route path="/providers" element={<ProtectedRoute><ProvidersPage /></ProtectedRoute>} />
          <Route path="/commission-codes" element={<ProtectedRoute><CommissionCodesPage /></ProtectedRoute>} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
